import * as React from "react"
import type { LucideIcon } from "lucide-react"
import { ArrowLeft } from "lucide-react"
import { cn } from "@shadcn/lib/utils"
import { IconButton } from "@shadcn/components/atoms/buttons/IconButton"

export interface CompactPageHeaderProps {
	/**
	 * Icon to display next to title
	 */
	icon: LucideIcon
	/**
	 * Page title
	 */
	title: string
	/**
	 * Optional subtitle displayed below the title
	 */
	subtitle?: string
	/**
	 * Action buttons (right side)
	 */
	actions?: React.ReactNode
	/**
	 * Additional class name
	 */
	className?: string
	/**
	 * Optional back button handler
	 * When provided, displays a back button before the icon
	 */
	onBack?: () => void
}

/**
 * CompactPageHeader - A compact page header with icon and title
 * Reusable across multiple page types (tabbed pages, sub-pages, etc.)
 * Can optionally display a back button when onBack is provided
 */
export function CompactPageHeader({
	icon: Icon,
	title,
	subtitle,
	actions,
	className,
	onBack,
}: Readonly<CompactPageHeaderProps>) {
	return (
		<>
			{/* SVG Gradient Definition */}
			<svg width="0" height="0" style={{ position: "absolute" }}>
				<defs>
					<linearGradient id="icon-gradient-hireara" x1="0" y1="0" x2="24" y2="24" gradientUnits="userSpaceOnUse">
						{/* <stop offset="0%" stopColor="var(--hirearapurple-500)" />
						<stop offset="100%" stopColor="var(--hirearablue-500)" /> */}
						<stop offset="0%" stopColor="var(--teal-900)" />
						<stop offset="100%" stopColor="var(--teal-400)" />
					</linearGradient>
				</defs>
			</svg>

			<div className={cn("flex items-center justify-between", className)}>
				<div className="flex items-center gap-3">
					{/* Back Button (optional) */}
					{onBack && <IconButton icon={ArrowLeft} onClick={onBack} aria-label="Go back" />}
					{/* Icon and Title */}
					<Icon className="h-6 w-6" style={{ stroke: "url(#icon-gradient-hireara)" }} />
					<div>
						<h1 className="text-xl font-semibold tracking-tight">{title}</h1>
						{subtitle && <p className="text-sm text-muted-foreground">{subtitle}</p>}
					</div>
				</div>
				{actions && <div className="flex items-center gap-3">{actions}</div>}
			</div>
		</>
	)
}
